import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MapPin } from 'lucide-react';
import { POINT_COLOR, type StationaryPoint } from '../../data/stationary-mock';
import {
  loadGoogleMaps,
  type GoogleMapInstance,
  type GoogleMapsApi,
  type GoogleMarker,
} from '../../maps/googleMaps';
import { readAppliedHex } from '../../theme/appearance';
import { useTheme } from '../../theme/ThemeContext';

export type MapStyleName = 'map' | 'satellite' | 'terrain';

export interface StationaryMapHandle {
  zoomBy(delta: number): void;
  fitAll(): void;
}

interface StationaryMapProps {
  points: StationaryPoint[];
  selectedId: string | null;
  mapStyle: MapStyleName;
  grouped?: boolean;
  onSelect(id: string): void;
}

interface MapSpot {
  key: string;
  latitude: number;
  longitude: number;
  color: StationaryPoint['color'];
  name: string;
  count: number;
  selectId: string;
  selected: boolean;
}

function toSpots(points: StationaryPoint[], grouped: boolean, selectedId: string | null): MapSpot[] {
  if (!grouped) {
    return points.map((point) => ({
      key: point.id,
      latitude: point.latitude,
      longitude: point.longitude,
      color: point.color,
      name: point.name,
      count: 1,
      selectId: point.id,
      selected: point.id === selectedId,
    }));
  }

  const groups = new Map<string, StationaryPoint[]>();
  points.forEach((point) => {
    const key = `${Math.round(point.latitude * 20)}:${Math.round(point.longitude * 20)}`;
    const list = groups.get(key) ?? [];
    list.push(point);
    groups.set(key, list);
  });

  return Array.from(groups.entries()).map(([key, group]) => {
    const focus = group.find((point) => point.id === selectedId) ?? group[0];
    return {
      key,
      latitude: group.reduce((sum, point) => sum + point.latitude, 0) / group.length,
      longitude: group.reduce((sum, point) => sum + point.longitude, 0) / group.length,
      color: focus.color,
      name: group.length === 1 ? focus.name : `${group.length} nearby points`,
      count: group.length,
      selectId: focus.id,
      selected: group.some((point) => point.id === selectedId),
    };
  });
}

function clusterUrl(count: number): string {
  const fill = readAppliedHex('--ml-primary', '#6d4dff');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36"><circle cx="18" cy="18" r="16" fill="${fill}"/><text x="18" y="23" text-anchor="middle" font-size="14" font-family="sans-serif" fill="#fff">${count}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#1b2230' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#c5cbe0' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1b2230' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c3548' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1b2230' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#24344d' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const BOUNDS = { north: 28.82, south: 28.38, west: 76.95, east: 77.58 };

const LABELS = [
  { name: 'Rohini', left: '18%', top: '18%' },
  { name: 'Pitampura', left: '28%', top: '28%' },
  { name: 'New Delhi', left: '42%', top: '46%' },
  { name: 'Gurugram', left: '16%', top: '72%' },
  { name: 'Noida', left: '68%', top: '58%' },
  { name: 'Ghaziabad', left: '74%', top: '32%' },
];

function project(latitude: number, longitude: number): { x: number; y: number } {
  const x = ((longitude - BOUNDS.west) / (BOUNDS.east - BOUNDS.west)) * 100;
  const y = ((BOUNDS.north - latitude) / (BOUNDS.north - BOUNDS.south)) * 100;
  return {
    x: Math.min(96, Math.max(4, x)),
    y: Math.min(94, Math.max(6, y)),
  };
}

function pinUrl(color: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><path fill="${color}" d="M16 1C8 1 1.5 7.4 1.5 15.2 1.5 26 16 41 16 41s14.5-15 14.5-25.8C30.5 7.4 24 1 16 1z"/><circle cx="16" cy="15" r="5.5" fill="#fff"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function fitPoints(map: GoogleMapInstance, api: GoogleMapsApi, points: StationaryPoint[]) {
  if (points.length === 0) return;
  const bounds = new api.LatLngBounds();
  points.forEach((point) => bounds.extend({ lat: point.latitude, lng: point.longitude }));
  if (!bounds.isEmpty()) map.fitBounds(bounds, 48);
}

const StationaryMap = forwardRef<StationaryMapHandle, StationaryMapProps>(function StationaryMap(
  { points, selectedId, mapStyle, grouped = false, onSelect },
  ref
) {
  const { theme } = useTheme();
  const canvasRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMapInstance | null>(null);
  const markersRef = useRef<GoogleMarker[]>([]);
  const pointsRef = useRef(points);
  const [api, setApi] = useState<GoogleMapsApi | null>(null);
  const [phase, setPhase] = useState<'loading' | 'google' | 'fallback'>('loading');
  const [scale, setScale] = useState(1);

  pointsRef.current = points;

  useEffect(() => {
    let cancelled = false;
    const host = window as Window & { gm_authFailure?: () => void };
    const previous = host.gm_authFailure;
    host.gm_authFailure = () => {
      previous?.();
      if (!cancelled) setPhase('fallback');
    };
    loadGoogleMaps().then((maps) => {
      if (cancelled) return;
      if (!maps) {
        setPhase('fallback');
        return;
      }
      setApi(maps);
      setPhase((current) => (current === 'fallback' ? current : 'google'));
    }).catch(() => {
      if (!cancelled) setPhase('fallback');
    });
    return () => {
      cancelled = true;
      host.gm_authFailure = previous;
    };
  }, []);

  useEffect(() => {
    if (phase !== 'google' || !api || !canvasRef.current) return undefined;
    const map = new api.Map(canvasRef.current, {
      center: { lat: 28.61, lng: 77.23 },
      zoom: 11,
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: 'greedy',
      keyboardShortcuts: false,
      backgroundColor: document.documentElement.dataset.theme === 'light' ? '#e7eef6' : '#151822',
    });
    mapRef.current = map;
    fitPoints(map, api, pointsRef.current);
    return () => {
      mapRef.current = null;
      if (canvasRef.current) canvasRef.current.replaceChildren();
    };
  }, [api, phase]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || phase !== 'google') return;
    map.setMapTypeId(mapStyle === 'map' ? 'roadmap' : mapStyle);
    map.setOptions({
      styles: theme === 'dark' && mapStyle === 'map' ? DARK_MAP_STYLE : [],
    });
  }, [mapStyle, theme, phase, api]);

  useEffect(() => {
    const map = mapRef.current;
    if (!api || !map || phase !== 'google') return undefined;
    markersRef.current.forEach((marker) => marker.setMap(null));
    const spots = toSpots(points, grouped, selectedId);
    markersRef.current = spots.map((spot) => {
      const clustered = spot.count > 1;
      const size = spot.selected ? 38 : 30;
      const height = clustered ? size : (spot.selected ? 50 : 40);
      const marker = new api.Marker({
        map,
        position: { lat: spot.latitude, lng: spot.longitude },
        title: spot.name,
        zIndex: spot.selected ? 20 : 1,
        icon: clustered
          ? {
            url: clusterUrl(spot.count),
            scaledSize: new api.Size(36, 36),
            anchor: new api.Point(18, 18),
          }
          : {
            url: pinUrl(POINT_COLOR[spot.color]),
            scaledSize: new api.Size(size, height),
            anchor: new api.Point(size / 2, height - 2),
          },
      });
      marker.addListener('click', () => onSelect(spot.selectId));
      return marker;
    });
    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
  }, [api, grouped, onSelect, phase, points, selectedId]);

  useImperativeHandle(ref, () => ({
    zoomBy(delta: number) {
      const map = mapRef.current;
      if (map && phase === 'google') {
        const current = map.getZoom() ?? 12;
        map.setZoom(Math.max(3, Math.min(19, current + delta)));
        return;
      }
      setScale((current) => Math.max(1, Math.min(2.2, Number((current + delta * 0.25).toFixed(2)))));
    },
    fitAll() {
      const map = mapRef.current;
      if (map && api && phase === 'google') {
        fitPoints(map, api, pointsRef.current);
        return;
      }
      setScale(1);
    },
  }), [api, phase]);

  if (phase === 'google') {
    return <div ref={canvasRef} className="sp-map__canvas" data-testid="map-google" />;
  }

  if (phase === 'loading') {
    return <div className="sp-map__canvas sp-skel" data-testid="skeleton-map-tiles" />;
  }

  return (
    <div className="sp-fallback" data-testid="map-fallback">
      <div className="sp-fallback__zoom" style={{ transform: `scale(${scale})` }}>
        <svg className="sp-fallback__svg" viewBox="0 0 800 520" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <rect className="sp-fallback__land" width="800" height="520" />
          <path className="sp-fallback__park" d="M40 40h150v80H40zM520 360h160v70H520zM180 300h90v60h-90z" />
          <path className="sp-fallback__river" d="M470-10c30 70-40 110 0 180s40 90-10 160 20 110 40 200" />
          <g className="sp-fallback__roads">
            <path d="M0 150h800M0 250h800M0 360h800M140 0v520M300 0v520M470 0v520M640 0v520" />
            <path d="M80 420c120-80 180-20 280-90 80-56 140-30 240 20" />
          </g>
        </svg>
        {LABELS.map((label) => (
          <span key={label.name} className="sp-fallback__label" style={{ left: label.left, top: label.top }}>
            {label.name}
          </span>
        ))}
        {toSpots(points, grouped, selectedId).map((spot) => {
          const place = project(spot.latitude, spot.longitude);
          return (
            <button
              key={spot.key}
              type="button"
              className={`sp-fallback__pin${spot.selected ? ' is-selected' : ''}`}
              style={{ left: `${place.x}%`, top: `${place.y}%`, color: POINT_COLOR[spot.color] }}
              aria-label={spot.name}
              aria-pressed={spot.selected}
              onClick={() => onSelect(spot.selectId)}
            >
              {spot.count > 1 ? (
                <span className="sp-cluster">{spot.count}</span>
              ) : (
                <MapPin size={spot.selected ? 30 : 24} fill="currentColor" strokeWidth={1.5} />
              )}
            </button>
          );
        })}
      </div>
      <p className="sp-map__note">Map preview. Live Google tiles load when the Maps key is valid.</p>
    </div>
  );
});

export default StationaryMap;
